"use client";

import type { Voice } from "@/app/_data/voices";
import type { IPrompt } from "@components/languages/types";

import { toast } from "@/app/_providers/toast";
import { Badge } from "@components/core/Badge";
import MediaProgressBar from "@components/core/MediaProgressBar";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { cn } from "@ui/lib/utils";
import { ICON, ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { ExpandIcon, Settings2Icon, ShrinkIcon } from "lucide-react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { Avatar } from "../../core/Avatar";
import { Button } from "../../core/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../core/Select";
import { playingStateIcon } from "../TTS/config";
import useHasScrolled from "../TTS/hooks";

enum Length {
  Long = "long",
  Medium = "medium",
  Short = "short",
}
enum Breed {
  Chihuahua = "chihuahua",
  Dachshund = "dachshund",
  GoldenRetriever = "golden-retriever",
}

const BARK_SOUNDS = {
  [Breed.Chihuahua]: {
    [Length.Short]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/chiahuahua-short.mp3",
    [Length.Medium]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/chiahuahua-medium.mp3",
    [Length.Long]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/chiahuahua-long.mp3",
  },
  [Breed.Dachshund]: {
    [Length.Short]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/dachshund-short.mp3",
    [Length.Medium]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/dachshund-medium.mp3",
    [Length.Long]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/dachshund-long.mp3",
  },
  [Breed.GoldenRetriever]: {
    [Length.Short]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/golden-retriever-short.mp3",
    [Length.Medium]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/golden-retriever-medium.mp3",
    [Length.Long]:
      "https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/golden-retriever-long.mp3",
  },
};

const PROMPTS: IPrompt[] = [
  {
    title: "Who's a good boy?",
    icon: ICON.CHAT_BUBBLE_20,
    text: "Who's the best puppy in the world? Yes you are! Want a treat? Those puppy eyes get me every time!",
  },
  {
    title: "Dinner time!",
    icon: ICON.CHAT_BUBBLE_20,
    text: "Hey buddy, dinner time! I've got your favorite food ready in your bowl. I know you've been waiting patiently all day.",
  },
  {
    title: "Going to the park",
    icon: ICON.CHAT_BUBBLE_20,
    text: "Wanna go to the park? The P-A-R-K? Oh, you know that word, don't you? Let me just grab my shoes and your leash.",
  },
];

const DOG_VOICES: Omit<Voice, "useCase">[] = [
  {
    name: "Chihuahua",
    voiceId: "chihuahua",
    image: "/assets/images/dogs/chihuahua.png",
    tags: [{ name: "Tiny but Mighty" }, { name: "Good Dog" }],
  },
  {
    name: "Dachshund",
    voiceId: "dachshund",
    image: "/assets/images/dogs/dachshund.png",
    tags: [{ name: "Long Boi" }, { name: "Good Dog" }],
  },
  {
    name: "Golden Retriever",
    voiceId: "golden-retriever",
    image: "/assets/images/dogs/golden-retriever.png",
    tags: [{ name: "Forever Puppy" }, { name: "Good Dog" }],
  },
];

function getDogImage(dog: Breed | undefined) {
  if (!dog) {
    return undefined;
  }
  return {
    src: `https://storage.googleapis.com/eleven-public-cdn/marketing_website/assets/text-to-bark/${dog}.png`,
    fit: "cover",
  };
}

const TextArea = ({
  className,
  text,
  setText,
  setRegenerate,
}: {
  className?: string;
  setRegenerate: (regenerate: boolean) => void;
  setText: (text: string) => void;
  text: string;
}) => {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <textarea
      className={cn("scrollbar-none flex-1 resize-none bg-none px-16 py-16 outline-none @5xl:px-24", className)}
      onChange={e => {
        setText(e.target.value);
        setRegenerate(true);
      }}
      placeholder={t("widget.placeholder")}
      value={text}
    />
  );
};

const ExamplePromptScroller = ({
  hasScrolled,
  scrollContainerRef,
  selectedLanguagePrompts,
  setText,
  generate,
  playingState,
  noPadding = false,
}: {
  generate: (text: string) => void;
  hasScrolled: boolean;
  noPadding?: boolean;
  playingState: string;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  selectedLanguagePrompts: IPrompt[] | undefined;
  setText: (text: string) => void;
}) => {
  return (
    <>
      <div className='pointer-events-none z-10 h-full w-0 select-none'>
        <div
          className={cn(
            "from-white h-full w-80 bg-gradient-to-r to-transparent transition-opacity duration-200",
            hasScrolled ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      <div
        className={cn("scrollbar-none relative flex gap-8 overflow-scroll", !noPadding && "py-16 pe-16")}
        ref={scrollContainerRef}
      >
        {selectedLanguagePrompts?.map(({ title, text }) => {
          return (
            <Button
              disabled={playingState === "loading"}
              iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
              key={title}
              onClick={() => {
                setText(text);
                generate(text);
              }}
              variant='elevated'
            >
              {title}
            </Button>
          );
        })}
      </div>

      <div className='pointer-events-none relative z-10 mr-10 ms-auto hidden h-full w-0 select-none @5xl:block'>
        <div className='to-white -ml-80 h-full w-80 bg-gradient-to-r from-transparent' />
      </div>
    </>
  );
};

const CharacterCounter = ({
  text,
  className,
  maxCharacters,
}: {
  className?: string;
  maxCharacters: number;
  text: string;
}) => {
  return (
    <span className={cn("f-ui-05 tabular-nums", text.length > maxCharacters && "text-negative", className)}>
      {text.length}/{maxCharacters}
    </span>
  );
};

const PlayButton = ({
  className,
  maxCharacters,
  playingState,
  playHandler,
  text,
  variant,
}: {
  className?: string;
  maxCharacters: number;
  playHandler: () => void;
  playingState: string;
  text: string;
  variant?: "default" | "sidebar";
}) => {
  return (
    <Button
      className={cn("p-0", variant === "sidebar" ? "w-full" : "ml-16 w-40", className)}
      disabled={!text || playingState === "loading" || text.length > maxCharacters}
      onClick={playHandler}
    >
      <span className='flex items-center gap-8' key={playingState}>
        {playingStateIcon[playingState]}
        {variant === "sidebar" && (playingState === "playing" ? "Pause" : "Play")}
      </span>
    </Button>
  );
};

const DogVoiceSelector = ({
  voices,
  selectedBreed,
  selectedBreedId,
  setSelectedBreedId,
  variant = "default",
  showTags = false,
}: {
  selectedBreed: Breed | undefined;
  selectedBreedId: string;
  setSelectedBreedId: (breedId: Breed) => void;
  showTags?: boolean;
  variant?: "default" | "sidebar";
  voices: Omit<Voice, "useCase">[];
}) => {
  const t = useTranslations("ClientKeys.TTS");
  const dogVoice = voices.find(voice => voice.voiceId === selectedBreedId);

  return (
    <div className='flex flex-col gap-4'>
      {variant === "sidebar" && <div className='f-ui-04'>{t("widget.voice")}</div>}
      <Select onValueChange={value => setSelectedBreedId(value as Breed)} value={selectedBreedId}>
        <SelectTrigger
          className={cn("w-[180px]", variant === "sidebar" && "w-full")}
          icon={<Avatar height={30} image={getDogImage(selectedBreed)} size='sm' width={30} />}
        >
          <SelectValue>{dogVoice?.name ?? ""}</SelectValue>
        </SelectTrigger>
        <SelectContent className='max-h-[350px]'>
          {voices.map(voice => {
            return (
              <SelectItem
                icon={<Avatar height={30} image={getDogImage(voice.voiceId as Breed)} size='sm' width={30} />}
                key={voice.voiceId}
                value={voice.voiceId}
              >
                {voice.name}
                {showTags && voice?.tags?.length !== 0 && (
                  <div className='ml-8 inline-flex flex-wrap gap-4'>
                    {voice?.tags?.map(tag => (
                      <Badge className='bg-overlay-dark' key={tag.name}>
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

type AnimationState = "full-screen" | "original" | "popping" | "popping-back";

export default function TextToBark() {
  const { hasScrolled, scrollContainerRef } = useHasScrolled();
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("ClientKeys.TTS");
  const [text, setText] = useState(PROMPTS[0].text);
  const [regenerate, setRegenerate] = useState(true);

  const [selectedBreedId, setSelectedBreedId] = useState(Breed.Chihuahua);
  const [selectedBreedPrompts, setSelectedBreedPrompts] = useState<IPrompt[] | undefined>(PROMPTS);

  const [isExpanded, setIsExpanded] = useState(false);
  const [animationState, setAnimationState] = useState<AnimationState>("original");
  const elementRect = useRef<{ height: number; left: number; top: number; width: number } | null>(null);
  const isMobile = useIsMobile();
  const maxCharacters = 500;

  const [playingState, setPlayingState] = useState("idle");
  const isInitialRender = useRef(true);

  useEffect(() => {
    // Skip the initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    setRegenerate(true);
    if (text) {
      generate(text);
    }
  }, [selectedBreedId, text]);

  function generate(textPrompt?: string) {
    if (!audioElementRef.current) return;
    setPlayingState("loading");
    try {
      const text = textPrompt || "";
      // Randomly select a bark length
      const lengthOptions = [Length.Short, Length.Medium, Length.Long];
      const length = lengthOptions[Math.floor(Math.random() * lengthOptions.length)];

      // Get the appropriate audio URL
      const audioUrl = BARK_SOUNDS[selectedBreedId][length];

      // Set the audio source and play
      audioElementRef.current.src = audioUrl;
      audioElementRef.current.play();
    } catch (e: unknown) {
      toast.error("Something went wrong", {
        description: "It looks like there was an issue while generating. Please try again later.",
      });

      setTimeout(() => {
        setPlayingState("idle");
      }, 1000);
    }
    setRegenerate(false);
  }

  function playHandler() {
    if (!audioElementRef.current) return;
    switch (playingState) {
      case "idle":
        if (regenerate) {
          generate();
        } else {
          audioElementRef.current.play();
        }
        break;
      case "playing":
        audioElementRef.current.pause();
        break;
      case "paused":
        if (regenerate) {
          generate();
        } else {
          audioElementRef.current.play();
        }
        break;
    }
  }

  useEffect(() => {
    if (isExpanded && containerRef.current) {
      // First capture the current position
      document.body.style.overflow = "hidden";

      const rect = containerRef.current.getBoundingClientRect();
      elementRect.current = {
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      };

      // First set to popping state (fixed at current position)
      setAnimationState("popping");

      // Then transition to full-screen
      setTimeout(() => {
        setAnimationState("full-screen");
      }, 50);
    } else if (!isExpanded && elementRect.current) {
      // First go to popping-back state (fixed at current position)
      setAnimationState("popping-back");

      // Then after animation completes, return to original
      setTimeout(() => {
        setAnimationState("original");
        elementRect.current = null;
        document.body.style.overflow = "auto";
      }, 300);
    }
  }, [isExpanded]);

  return (
    <>
      <audio
        autoPlay
        className='h-24 w-24 border'
        onEnded={() => setPlayingState("idle")}
        onPause={() => setPlayingState("paused")}
        onPlay={() => setPlayingState("playing")}
        ref={audioElementRef}
      />
      <m.div
        animate={{
          position: animationState === "original" ? "relative" : "fixed",
          top: animationState === "full-screen" ? 0 : (elementRect.current?.top ?? "auto"),
          left: animationState === "full-screen" ? 0 : (elementRect.current?.left ?? "auto"),
          width: animationState === "full-screen" ? "100vw" : (elementRect.current?.width ?? "100%"),
          height: animationState === "full-screen" ? "100vh" : (elementRect.current?.height ?? "100%"),
          zIndex: animationState === "original" ? "auto" : 9999,
        }}
        className='flex overflow-hidden bg-default'
        initial={false}
        ref={containerRef}
        transition={{
          // No animation for entering/leaving original state
          duration: animationState === "original" || animationState === "popping" ? 0 : 0.2,
          // Use spring for expanding, tween for collapsing
          type: animationState === "popping-back" ? "tween" : "spring",
          // Spring settings (used when expanding)
          stiffness: 400,
          damping: 25,
          restDelta: 0.001,
        }}
      >
        <>
          <div className='flex w-full flex-col overflow-x-hidden'>
            <TextArea className='p-36 @5xl:px-36' setRegenerate={setRegenerate} setText={setText} text={text} />

            <MediaProgressBar className='order-1 mx-0 @5xl:order-none' mediaElement={audioElementRef.current || null} />
            <div className='relative flex items-center p-16 md:p-24'>
              <ExamplePromptScroller
                generate={generate}
                hasScrolled={hasScrolled}
                noPadding
                playingState={playingState}
                scrollContainerRef={scrollContainerRef}
                selectedLanguagePrompts={selectedBreedPrompts}
                setText={setText}
              />
              {!isMobile && <CharacterCounter className='ms-auto' maxCharacters={maxCharacters} text={text} />}
            </div>
            {isMobile && (
              <div className='order-2 flex items-center justify-between gap-12 p-16 @5xl:hidden'>
                <DogVoiceSelector
                  selectedBreed={selectedBreedId}
                  selectedBreedId={selectedBreedId}
                  setSelectedBreedId={setSelectedBreedId}
                  voices={DOG_VOICES}
                />
                <CharacterCounter className='ms-auto' maxCharacters={maxCharacters} text={text} />
                <PlayButton
                  className='ml-0'
                  maxCharacters={maxCharacters}
                  playHandler={playHandler}
                  playingState={playingState}
                  text={text}
                />
              </div>
            )}
          </div>
          {!isMobile && (
            <div className='relative flex w-2/5 flex-col gap-20 overflow-y-auto border-l border-solid p-24'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-8'>
                  <Settings2Icon size={16} />
                  <span className='f-ui-03'>{t("widget.voice_settings")}</span>
                </div>

                <Button
                  className='h-40 w-40 p-0'
                  onClick={() => setIsExpanded(!isExpanded)}
                  size='sm'
                  variant='elevated'
                >
                  {isExpanded ? <ShrinkIcon size={16} /> : <ExpandIcon size={16} />}
                </Button>
              </div>
              <DogVoiceSelector
                selectedBreed={selectedBreedId}
                selectedBreedId={selectedBreedId}
                setSelectedBreedId={setSelectedBreedId}
                showTags
                variant='sidebar'
                voices={DOG_VOICES}
              />
              <div className='mt-auto flex gap-8'>
                <PlayButton
                  maxCharacters={maxCharacters}
                  playHandler={playHandler}
                  playingState={playingState}
                  text={text}
                  variant='sidebar'
                />
              </div>
            </div>
          )}
        </>
      </m.div>
    </>
  );
}
